
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import ProfileAvatar from "./ProfileAvatar";
import ProfileTextField from "./form/ProfileTextField";
import ProfileTextArea from "./form/ProfileTextArea";
import ProfileFormActions from "./form/ProfileFormActions";
import ProfileLocationFields from "./form/ProfileLocationFields";

interface ProfileFormProps {
  profile: {
    full_name: string;
    username: string;
    bio: string;
    interests: string;
    grade_level: string;
    school: string;
    avatar_url?: string;
    country?: string;
    state?: string;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  uploading: boolean;
  autoSaved?: boolean;
  handleImageUpload?: (url: string) => void;
  savingState?: 'idle' | 'saving' | 'saved' | 'error';
  isProfileComplete?: boolean;
}

const ProfileForm = ({
  profile,
  handleChange,
  handleSubmit,
  uploading,
  handleImageUpload,
  savingState = 'idle',
  isProfileComplete = false
}: ProfileFormProps) => {
  const { user } = useAuth();
  
  const handleAvatarChange = (url: string) => {
    if (handleImageUpload) {
      handleImageUpload(url);
    }
  };
  
  return (
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <ProfileAvatar 
        avatarUrl={profile.avatar_url} 
        onImageUpload={handleAvatarChange}
        uploading={uploading}
      />
      
      <form onSubmit={handleSubmit} className="space-y-6 flex-1">
        <ProfileTextField
          id="full_name"
          name="full_name"
          label="Full Name"
          value={profile.full_name}
          onChange={handleChange}
          placeholder="Enter your full name"
          required
          disabled={uploading}
        />

        <ProfileTextField
          id="username"
          name="username"
          label="Username"
          value={profile.username}
          onChange={handleChange}
          placeholder="Choose a username"
          required
          disabled={uploading}
        />

        <ProfileLocationFields
          country={profile.country || ""}
          state={profile.state || ""}
          onChange={handleChange}
          disabled={uploading}
        />

        <ProfileTextField
          id="school"
          name="school"
          label="School"
          value={profile.school}
          onChange={handleChange}
          placeholder="Your school name"
          disabled={uploading}
        />

        <ProfileTextField
          id="grade_level"
          name="grade_level"
          label="Grade Level"
          value={profile.grade_level}
          onChange={handleChange}
          placeholder="9th, 10th, 11th, 12th, etc."
          disabled={uploading}
        />

        <ProfileTextArea
          id="bio"
          name="bio"
          label="Bio"
          value={profile.bio}
          onChange={handleChange}
          placeholder="Tell us a bit about yourself"
          rows={4}
          required
          disabled={uploading}
        />

        <ProfileTextArea
          id="interests"
          name="interests"
          label="Interests"
          value={profile.interests}
          onChange={handleChange}
          placeholder="What are you interested in? (e.g., coding, art, science)"
          rows={3}
          required
          disabled={uploading}
        />

        <ProfileFormActions 
          savingState={savingState}
          uploading={uploading}
          isProfileComplete={isProfileComplete}
        />
      </form>
    </div>
  );
};

export default ProfileForm;
